import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const rules = [
    { ok: password.length >= 12, label: "At least 12 characters" },
    { ok: /[A-Z]/.test(password), label: "An uppercase letter" },
    { ok: /[a-z]/.test(password), label: "A lowercase letter" },
    { ok: /\d/.test(password), label: "A number" },
    { ok: /[^A-Za-z0-9]/.test(password), label: "A special character" },
  ];
  const allValid = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) { toast.error("Please meet all password requirements"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} className="glass glass-cyan rounded-2xl p-7 w-full max-w-md" data-testid="reset-form">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-cyan-400" /><h1 className="font-head font-bold text-lg">Set a new password</h1></div>
        {!token ? (
          <p className="text-sm text-amber-300 font-mono mt-3" data-testid="reset-no-token">This reset link is missing its token. Request a new one.</p>
        ) : (
          <>
            <div className="space-y-1 mt-4"><Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="bg-slate-900/60 border-white/10" required /></div>
            <ul className="mt-3 space-y-1" data-testid="password-rules">
              {rules.map((r) => (
                <li key={r.label} className={`flex items-center gap-2 text-[11px] font-mono ${r.ok ? "text-emerald-300" : "text-muted-foreground"}`}>
                  <span className="w-3 text-center">{r.ok ? "✓" : "•"}</span>{r.label}
                </li>
              ))}
            </ul>
            <Button type="submit" disabled={busy || !allValid} data-testid="reset-submit" className="w-full mt-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold disabled:opacity-50">{busy ? "Updating…" : "Update password"}</Button>
          </>
        )}
        <p className="text-xs text-muted-foreground mt-4 text-center font-mono"><Link to="/login" className="text-cyan-400 hover:text-cyan-300">Back to sign in</Link></p>
      </form>
    </div>
  );
}
